import Component from '@glimmer/component';

// ---------------------------------------------------------------------------
// Avatar – reimagined, zero-Frontile avatar component
// ---------------------------------------------------------------------------

/**
 * Size options for the avatar
 */
export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Shape options for the avatar
 */
export type AvatarShape = 'circle' | 'square';

export interface AvatarSignature {
  Args: {
    /** Full name — initials derived from first two words */
    name?: string;
    /** First name for initials (used when @name not provided) */
    firstName?: string;
    /** Last name for initials (used when @name not provided) */
    lastName?: string;
    /** Image URL — when provided, renders an <img> instead of initials */
    src?: string | null;
    /** Accessible label for screen readers */
    alt?: string;
    /** Size variant @default 'md' */
    size?: AvatarSize;
    /** Shape variant @default 'circle' */
    shape?: AvatarShape;
    /** Background color class (e.g. 'bg-violet-500'). Overrides the auto-generated color. */
    color?: string;
  };
  Element: HTMLSpanElement;
}

// ---------------------------------------------------------------------------
// Style maps
// ---------------------------------------------------------------------------

const SIZE_CLASSES: Record<AvatarSize, string> = {
  xs: 'w-5 h-5 text-[10px]',
  sm: 'w-7 h-7 text-xs',
  md: 'w-9 h-9 text-sm',
  lg: 'w-11 h-11 text-base',
  xl: 'w-14 h-14 text-lg',
};

const SHAPE_CLASSES: Record<AvatarShape, string> = {
  circle: 'rounded-full',
  square: 'rounded-lg',
};

// Deterministic palette — maps a name to a consistent color
const AVATAR_PALETTE = [
  'bg-rose-600',
  'bg-cyan-600',
  'bg-amber-600',
  'bg-emerald-600',
  'bg-violet-600',
  'bg-yellow-600',
  'bg-sky-600',
  'bg-pink-600',
  'bg-purple-700',
  'bg-teal-600',
  'bg-orange-600',
  'bg-indigo-600',
  'bg-lime-600',
  'bg-fuchsia-600',
];

/**
 * Simple hash to pick a stable color from a string.
 */
function nameToColor(input: string): string {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = input.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % AVATAR_PALETTE.length;
  return AVATAR_PALETTE[index]!;
}

/**
 * Avatar – displays user initials or a photo in a round (or square) badge.
 *
 * Fixes from Frontile version:
 * - Thinner ring (ring-1 instead of ring-2)
 * - Better default sizing
 * - Built-in deterministic color from name
 *
 * @example Initials from name
 * ```gts
 * import Avatar from 'tio-reimagined/components/avatar';
 * <Avatar @name="Jane Doe" />
 * ```
 *
 * @example Image
 * ```gts
 * <Avatar @src="/photo.jpg" @alt="Jane Doe" @size="lg" />
 * ```
 *
 * @example With explicit color
 * ```gts
 * <Avatar @name="Jane Doe" @color="bg-indigo-600" @size="sm" />
 * ```
 */
export default class Avatar extends Component<AvatarSignature> {
  get size(): AvatarSize {
    return this.args.size ?? 'md';
  }

  get shape(): AvatarShape {
    return this.args.shape ?? 'circle';
  }

  get initials() {
    const { name, firstName, lastName } = this.args;
    if (name) {
      return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w.charAt(0))
        .join('')
        .toUpperCase();
    }
    return [firstName?.charAt(0), lastName?.charAt(0)].filter(Boolean).join('').toUpperCase();
  }

  get showImage() {
    return Boolean(this.args.src);
  }

  get showInitials() {
    return !this.args.src && Boolean(this.args.name || this.args.firstName || this.args.lastName);
  }

  get bgColor() {
    if (this.args.color) return this.args.color;
    const seed = this.args.name || `${this.args.firstName ?? ''}${this.args.lastName ?? ''}`;
    return seed ? nameToColor(seed) : 'bg-gray-400';
  }

  /** Accessible label — falls back to @name or derived full name when @alt is omitted. */
  get altText() {
    if (this.args.alt) return this.args.alt;
    if (this.args.name) return this.args.name;
    const parts = [this.args.firstName, this.args.lastName].filter(Boolean);
    return parts.length ? parts.join(' ') : '';
  }

  get sizeClass() {
    return SIZE_CLASSES[this.size];
  }

  get shapeClass() {
    return SHAPE_CLASSES[this.shape];
  }

  <template>
    <span
      data-test-avatar
      data-test-avatar-size={{this.size}}
      class="inline-flex items-center justify-center font-semibold text-white ring-1 ring-white
        {{this.bgColor}}
        {{this.sizeClass}}
        {{this.shapeClass}}"
      ...attributes
    >
      {{#if this.showImage}}
        <img
          src={{@src}}
          alt={{this.altText}}
          class="w-full h-full object-cover {{this.shapeClass}}"
        />
      {{else if this.showInitials}}
        <span role="img" aria-label={{this.altText}}>{{this.initials}}</span>
      {{/if}}
    </span>
  </template>
}
